<script>
import { products } from '@site/data/products'
import AppIcon from '@site/components/common/AppIcon.vue'

export default {
  name: 'DualProductSection',
  components: { AppIcon },
  data() {
    return { products }
  },
}
</script>

<template>
  <section class="section section--white" id="dual-product">
    <div class="container">
      <div class="section-head reveal">
        <span class="section-eyebrow">双产品选择</span>
        <h2>一套生意能力，两种选择：标准、还是旗舰</h2>
        <p>航富通与航富通 Nova 覆盖同一类门店，核心能力相同；Nova 是旗舰版，更快、更省心、更快迭代</p>
      </div>

      <div class="grid grid--2">
        <router-link
          v-for="(p, i) in products"
          :key="p.type"
          :to="p.link"
          class="dual-card reveal"
          :class="{ 'dual-card--nova': p.isFlagship }"
        >
          <div class="dual-card__mesh">
            <span class="dual-card__mesh-blob dual-card__mesh-blob--1"></span>
            <span class="dual-card__mesh-blob dual-card__mesh-blob--2"></span>
          </div>
          <div class="dual-card__top">
            <span class="dual-card__mark">
              <AppIcon :name="p.isFlagship ? 'rocket' : 'store'" :size="22" />
            </span>
            <span class="dual-card__badges">
              <span v-if="p.isNew" class="dual-card__newbadge">新品</span>
              <span class="dual-card__badge">{{ p.isFlagship ? '旗舰版' : '标准版' }}</span>
            </span>
          </div>
          <h3>{{ p.name }}</h3>
          <p class="dual-card__tagline">{{ p.tagline }}</p>
          <p class="dual-card__audience">{{ p.audience }}</p>
          <p class="dual-card__one-liner">{{ p.oneLiner }}</p>
          <div class="dual-card__footer">
            <span class="dual-card__cta">
              了解{{ p.name.replace('航富通', '') || '航富通' }}
              <AppIcon name="arrow" :size="16" />
            </span>
          </div>
        </router-link>
      </div>
    </div>
  </section>
</template>


<style lang="scss" scoped>
.dual-card {
  position: relative;
  overflow: hidden;
  padding: 48px 44px;
  border-radius: $radius-xl;
  color: $color-text;
  background: linear-gradient(135deg, #F4EDFF 0%, #FBE9F6 100%);
  border: 1px solid rgba(139, 92, 246, 0.16);
  box-shadow: 0 8px 26px rgba(139, 92, 246, 0.10);
  display: flex;
  flex-direction: column;
  transition: transform $motion-base $ease-out, box-shadow $motion-base $ease-out, border-color $motion-base $ease-out;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(139, 92, 246, 0.30);
    box-shadow: 0 14px 40px rgba(139, 92, 246, 0.16), 0 22px 48px rgba(236, 72, 153, 0.12);
  }

  &--nova {
    background: linear-gradient(135deg, #FBE9F6 0%, #F1E9FF 100%);
    border-color: rgba(236, 72, 153, 0.20);

    &:hover {
      border-color: rgba(236, 72, 153, 0.34);
      box-shadow: 0 14px 40px rgba(236, 72, 153, 0.16), 0 22px 48px rgba(139, 92, 246, 0.12);
    }
  }

  &__mesh {
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  &__mesh-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(50px);
    &--1 { width: 260px; height: 260px; top: -50px; right: -40px; background: rgba(139, 92, 246, 0.10); }
    &--2 { width: 200px; height: 200px; bottom: -40px; left: -30px; background: rgba(236, 72, 153, 0.08); }
  }

  &__top {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;
  }

  &__mark {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: linear-gradient(135deg, #8B5CF6, #EC4899);
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-white;
    box-shadow: 0 6px 16px rgba(139, 92, 246, 0.22);

    svg { width: 22px; height: 22px; }
  }

  &__badges {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: $weight-semibold;
    letter-spacing: 0.08em;
    color: $color-primary;
    border: 1px solid rgba(139, 92, 246, 0.28);
    border-radius: $radius-full;
    padding: 6px 16px;
    background: rgba(255, 255, 255, 0.7);
  }

  &__newbadge {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    font-weight: $weight-bold;
    letter-spacing: 0.04em;
    color: $color-white;
    background: linear-gradient(120deg, $color-dopamine-orange, $color-dopamine-pink);
    border-radius: $radius-full;
    padding: 6px 14px;
    box-shadow: 0 4px 12px rgba(251, 146, 60, 0.3);
  }

  h3 { position: relative; color: $color-text; font-size: 32px; font-weight: $weight-black; margin-bottom: 10px; letter-spacing: -0.02em; }

  &__tagline { position: relative; font-size: 17px; color: $color-text; font-weight: $weight-medium; margin-bottom: 12px; }
  &__audience { position: relative; font-size: 13px; color: $color-text-muted; font-weight: $weight-semibold; letter-spacing: 0.06em; margin-bottom: 14px; }
  &__one-liner { position: relative; font-size: 15px; color: $color-text-muted; line-height: 1.65; margin-bottom: 30px; }

  &__footer { position: relative; margin-top: auto; }

  &__cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: $color-primary;
    font-weight: $weight-semibold;

    svg { color: $color-primary; transition: transform $motion-fast $ease-out; }

    &:hover svg { transform: translateX(4px); }
  }

  @media (max-width: $bp-sm) {
    padding: 36px 30px;
    h3 { font-size: 26px; }
  }
}
</style>



// WEBPACK FOOTER //
// src/site/components/home/DualProductSection.vue